/*
  ── A DELIBERATE FORK of <IndexTable> ────────────────────────────────────────────────

  Copied, not extended, on the product owner's call: the plantilla list needs columns and
  behaviour of its own, and the shared table backs every other module in the product. A prop
  added here cannot reach them, and a mistake here cannot break them.

  What that costs, stated so nobody has to discover it: this file does NOT inherit
  improvements to the shared table. When a bug is fixed there — a sticky-header z-index, a
  resize ceiling, an overflow rule — it has to be fixed here too. Diff the two before
  changing either.
*/
import { Fragment } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { MenuItem } from '../../../../components/Menu';
import { SkeletonRows } from '../../../../components/Skeleton';
import { AnchorCellContext } from '../../../../components/table/anchorCell';
import { IndexTableSurfaceContext } from '../../../../components/table/indexTableSurface';
import { RowActionMenu } from '../../../../components/table/RowActionMenu';
import type { OtherInfoData } from '../../../../components/table/OtherInfoModal';
import { useColumnWidths } from '../../../../components/table/useColumnWidths';
import { useRowHeight } from '../../../../components/table/useRowHeight';
import type { ColumnDef } from '../../../../components/index-page/types';
import { Checkbox } from '../../../../components/inputs/Checkbox';

const SELECT_WIDTH = 44;
const ROW_NUMBER_WIDTH = 52;
const ACTIONS_WIDTH = 56;

/**
 * How a row ORDINAL is set: mono, tabular, small and muted.
 *
 * Exported because two columns carry one — the `#` gutter numbers rows down the page, and a
 * `groupCounter` column numbers them within their group. They are the same kind of value and
 * have to read as one, so the look is defined here rather than retyped at the second call
 * site, where it would drift.
 *
 * Muted and smaller than the data beside it on purpose: an ordinal is how you refer to a row,
 * not something the row tells you, so it must not compete with the values it indexes.
 */
export const ROW_ORDINAL = 'font-mono tabular-nums text-xs text-slate-400';

/** One header row's own look, shared by the group row and the column row. */
const HEAD_ROW =
  'bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal';

/**
 * A plantilla column: the shared `ColumnDef`, plus the three things this table can do that
 * the standard one cannot.
 */
export type PlantillaColumnDef<T> = ColumnDef<T> & {
  /**
   * Files this column under a spanning header cell of this name.
   *
   * Columns sharing a group must be declared CONSECUTIVELY — the group is drawn as one cell
   * across a run, and a run with a gap in it would need two cells with one name, which is
   * two groups wearing one label.
   */
  group?: string;
  /**
   * Merges consecutive rows that answer with the same key into one cell.
   *
   * Only ever correct on a list ordered by that key: the merge groups ADJACENT rows, so on a
   * list sorted by something else the same value would produce several cells rather than one
   * — which reads as a rendering fault rather than as grouping.
   */
  mergeKey?: (row: T) => string;
  /**
   * Turns the cell's text sideways — rotated a quarter turn and reading bottom-to-top — for
   * a column narrow enough to label a run of rows without costing the table a screen.
   *
   * **Sideways, not `text-orientation: upright`.** Upright keeps each glyph facing forward
   * and stacks them one per line, so "IT 00EC6D" came out as a column of nine letters two
   * characters wide. Sideways rotates the whole line, which is what a row-group label is.
   */
  sideways?: boolean;
  /**
   * Numbers the row within its merged group rather than within the page.
   *
   * The row's ordinal arrives as `render`'s SECOND argument in place of the page-wide row
   * number — the same signature, a different question: the `#` gutter answers "which row of
   * the list is this", and this answers "which post of this unit is this". Both are worth
   * having, and neither substitutes for the other.
   *
   * With no merged column to group by it counts from the top of the page, which is the only
   * honest answer when there are no groups.
   */
  groupCounter?: boolean;
};

/**
 * The runs of consecutive columns that share a group, and the ungrouped ones between them.
 *
 * One pass, so the header cannot disagree with the columns underneath it.
 */
function groupRuns<T>(columns: PlantillaColumnDef<T>[]) {
  const runs: { group: string | null; start: number; span: number }[] = [];

  for (const [index, column] of columns.entries()) {
    const previous = runs.at(-1);

    if (previous && column.group != null && previous.group === column.group) {
      previous.span += 1;
      continue;
    }

    runs.push({ group: column.group ?? null, start: index, span: 1 });
  }

  return runs;
}

/**
 * How many rows each merged cell covers, by the index of the row that starts the run.
 *
 * A row absent from the map is inside a run that started earlier and renders no cell at all
 * — the one above it already spans this row.
 */
function mergeSpans<T>(rows: T[], keyOf: (row: T) => string): Map<number, number> {
  const spans = new Map<number, number>();
  let start = 0;

  for (const [index, row] of rows.entries()) {
    if (index === 0) {
      spans.set(0, 1);
      continue;
    }

    if (keyOf(row) === keyOf(rows[start]!)) {
      spans.set(start, (spans.get(start) ?? 1) + 1);
    } else {
      start = index;
      spans.set(start, 1);
    }
  }

  return spans;
}

/**
 * The medicine-table format — reference §4. One tabular look everywhere.
 *
 * Fixed layout with a colgroup, a page-aware `#` gutter, resizable columns, a sticky
 * header, a sticky totals `<tfoot>`, opt-in multi-select, and the Actions column always
 * last and pinned right.
 *
 * The anchor column — the first after the `#` gutter — is the row's identity and its click
 * target. Whether it is ALSO pinned to the left edge is a separate question with a separate
 * prop, `freezeAnchor`, off by default.
 */
export function PlantillaIndexTable<T>({
  module,
  columns,
  rows,
  rowKey,
  firstRow,
  loading,
  sort,
  onToggleSort,
  otherInfo,
  rowActions,
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
  selectable = false,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  onRowOpen,
  freezeAnchor = false,
}: {
  module: string;
  columns: PlantillaColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  firstRow: number;
  loading: boolean;
  sort: string | null;
  onToggleSort: (field: string) => void;
  otherInfo: (row: T) => OtherInfoData | null;
  rowActions?: (row: T) => MenuItem[];
  onEdit?: (row: T) => void;
  editPermission?: string;
  onDelete?: (row: T) => void;
  deletePermission?: string;
  deleteLabel?: (row: T) => string;
  deleteDisabledReason?: (row: T) => string | null;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onToggleRow?: (key: string) => void;
  onToggleAll?: () => void;
  /**
   * Opens the record from the anchor column. Supplying it is what makes that column
   * clickable — without it the cell stays darker but inert, never a false affordance.
   */
  onRowOpen?: (row: T) => void;
  /**
   * Pins the anchor column to the left edge while the table scrolls sideways. Off by
   * default: it earns its keep only on a table wide enough to lose sight of its subject,
   * and costs a visible seam plus a stacking context on every other one.
   */
  freezeAnchor?: boolean;
}) {
  const defaults = columns.map((c) => c.width);
  const { widths, startResize } = useColumnWidths(module, columns);

  /*
    Row height is the other half of the same control: both axes are dragged, both persist
    per table, and both are the reader's call rather than the page's.
  */
  const { height: rowHeight, startResize: startRowResize } = useRowHeight(module);

  const [sortField, sortDirection] = sort ? sort.split(':') : [null, null];

  // Any column declaring a footer turns the totals row on for the whole table.
  const hasFooter = columns.some((c) => c.footer);

  /*
    The table is exactly as wide as its columns add up to.

    It used to be `width: max-content`, which sounds equivalent and is not: max-content sizes
    the table from its CONTENT, so a value wider than its column simply widened the column —
    the colgroup asked for 80px and got 331px. Column widths were advisory, a drag could be
    silently undone by one long value, and no cell ever overflowed, so neither the ellipsis
    nor the wrap rule could ever fire.

    With an explicit total, `minWidth: 100%` still stretches a narrow table to fill the panel,
    and the browser hands that slack to the width-less soft-flex column — which is the
    behaviour the flex column was always meant to have.
  */
  const totalWidth =
    (selectable ? SELECT_WIDTH : 0) +
    ROW_NUMBER_WIDTH +
    columns.reduce((sum, column, index) => sum + (widths[index] ?? column.width), 0) +
    ACTIONS_WIDTH;

  const selected = selectedKeys ?? new Set<string>();
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const someSelected = rows.some((r) => selected.has(rowKey(r))) && !allSelected;

  // The leading gutters are always pinned; the anchor joins them only if asked.
  const frozenOffset = selectable ? SELECT_WIDTH : 0;

  const runs = groupRuns(columns);
  const headRows = runs.some((run) => run.group !== null) ? 2 : 1;

  /*
    Every vertical line a group draws, from its left edge to its right, at every row.

    A column is in the set when its own LEFT border is part of a group's outline — so that is
    each member of a run, plus the column immediately after it, which is what draws the run's
    closing edge. All of them are a step darker than an ordinary column divider.

    The whole outline rather than only the dividers between the members: a group is a block of
    columns that belong together, and an outline that was dark inside and faint at its edges
    read as three loose columns that happened to share a heading. Darkening the run's own two
    sides is what makes the block visible all the way down the table.

    A run ending at the last column needs nothing extra — the Actions gutter's own left border
    closes it.
  */
  const groupBorder = new Set<number>();

  for (const run of runs) {
    if (run.group === null) {
      continue;
    }

    for (let offset = 0; offset <= run.span; offset += 1) {
      groupBorder.add(run.start + offset);
    }
  }

  /*
    The anchor is the first column that is not a merged group — a merged cell belongs to the
    run of rows it covers rather than to any one of them, so it cannot be a row's click
    target. Without this the merged Unit column would become the anchor and the reader would
    open a record by clicking a cell that spans four of them.
  */
  const anchorIndex = Math.max(
    columns.findIndex((column) => !column.mergeKey && !column.groupCounter),
    0,
  );

  /** The run spans for each merged column, keyed by column index. */
  const merged = new Map<number, Map<number, number>>();

  for (const [index, column] of columns.entries()) {
    if (column.mergeKey) {
      merged.set(index, mergeSpans(rows, column.mergeKey));
    }
  }

  /*
    Where each merged group starts, taken from the FIRST merged column — the one the rows are
    grouped by. Its keys are the row indices that open a run, which is exactly where a
    separator belongs.

    Read from the same map the merged cells are drawn from, so a separator can never land
    anywhere but on a boundary the cells agree with.
  */
  const groupStarts = merged.size === 0 ? null : [...merged.values()][0];

  /** Every column plus the gutters, for a row that spans the whole table. */
  const fullSpan = columns.length + (selectable ? 3 : 2);

  /*
    Each row's ordinal within its own group, resolved in one pass from the same starts the
    separators and the merged cells read. Walking backwards from each row to find its start
    would be the same answer computed n times.
  */
  const groupOrdinals: number[] = [];
  let ordinal = 0;

  for (let index = 0; index < rows.length; index += 1) {
    ordinal = groupStarts?.has(index) ? 1 : ordinal + 1;
    groupOrdinals.push(ordinal);
  }

  /**
   * One column's header cell — the label, its sort control and its resize grip.
   *
   * Shared by both header rows: an ungrouped column renders it spanning two, a grouped one
   * renders it in the second. Two copies of this JSX is how the sort button ends up on one
   * row and not the other.
   */
  function columnHead(column: PlantillaColumnDef<T>, index: number, rowSpan: number) {
    const isSorted = sortField === column.key;
    const isFrozen = freezeAnchor && index === anchorIndex;

    return (
      <th
        key={column.key}
        scope="col"
        rowSpan={rowSpan}
        aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
        className={[
          'relative bg-slate-100 px-3 py-2',
          /*
            Whether this cell draws a left border is decided by its COLUMN INDEX, never by
            `first:`.

            `first:border-l-0` asks "is this the first cell of its row" — which in the second
            header row is the first GROUPED column, not the first column of the table. That is
            what left Vacant with no left edge while every cell below it had one: the group's
            outline started a row late.
          */
          index === 0 ? '' : 'border-l',
          index === 0
            ? ''
            : groupBorder.has(index)
              // The same divider as the cells below it, or the line breaks at the header seam.
              ? 'border-slate-300'
              : 'border-slate-200',
          column.align === 'right' ? 'text-right' : 'text-left',
          isFrozen ? 'sticky z-30' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
      >
        {column.sortable ? (
          <button
            type="button"
            id={`${module}-sort-${column.key}`}
            onClick={() => onToggleSort(column.key)}
            /* The glyph sits on the column's OUTER RIGHT — see `<IndexTable>`, which this
               header is a copy of. Both move together or this page's arrows sit somewhere
               else from every other index page's. */
            className={[
              'flex w-full items-center gap-1 uppercase tracking-wider transition hover:text-slate-700',
              column.align === 'right' ? 'justify-end' : 'justify-between',
            ].join(' ')}
          >
            <span className="min-w-0">{column.header}</span>
            {isSorted ? (
              sortDirection === 'asc' ? (
                <ChevronUp className="h-3 w-3 shrink-0" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
              )
            ) : (
              <ChevronsUpDown className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
            )}
          </button>
        ) : (
          column.header
        )}

        {/*
          Drag handle on the right edge. No adjacent-column compensation — the table widens
          instead, which is what makes a drag feel predictable.
        */}
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${column.header}`}
          onPointerDown={(e) => startResize(index, e)}
          className="absolute -right-0.5 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-300"
        />
      </th>
    );
  }

  return (
    // Declares the surface so cells can refuse shapes that belong to other table contexts.
    <IndexTableSurfaceContext.Provider value={true}>
      {/*
        Stretched to the panel ONLY when there is a sticky tfoot to hold on the bottom edge.

        `height: 100%` on a table hands its surplus to the ROW boxes, distributed across
        them: a two-row list in a 700px panel rendered two 320px rows. So the stretch is now
        conditional, and the spacer row at the end of the tbody absorbs the slack — the tfoot
        still sits on the bottom edge of a short table, and the rows above it keep the height
        they asked for.
      */}
      <table
        className="border-collapse"
        style={{
          tableLayout: 'fixed',
          width: totalWidth,
          minWidth: '100%',
          height: hasFooter ? '100%' : undefined,
        }}
      >
        <colgroup>
          {selectable ? <col style={{ width: SELECT_WIDTH }} /> : null}
          <col style={{ width: ROW_NUMBER_WIDTH }} />
          {columns.map((column, index) => (
            <col
              key={column.key}
              // The soft-flex anchor stays width-less until dragged, so it absorbs slack.
              style={
                column.flex && widths[index] === defaults[index]
                  ? undefined
                  : { width: widths[index] ?? column.width }
              }
            />
          ))}
          <col style={{ width: ACTIONS_WIDTH }} />
        </colgroup>

        <thead className="sticky top-0 z-20">
          <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
            {selectable ? (
              <th
                scope="col"
                /*
                  Spans BOTH header rows when there is a group row above the columns. Without
                  it the second row's cells flow into this slot and the first grouped column
                  is drawn under the checkbox — which is exactly what happened.
                */
                rowSpan={headRows}
                className="sticky left-0 z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
                style={{ width: SELECT_WIDTH }}
              >
                <Checkbox
                  id={`${module}-select-all`}
                  name={`${module}-select-all`}
                  checked={allSelected}
                    indeterminate={someSelected}
                  onChange={onToggleAll}
                  aria-label="Select all rows on this page"
                />
              </th>
            ) : null}

            {/* The # gutter stays put so a row keeps its number while scrolling sideways. */}
            <th
              scope="col"
              rowSpan={headRows}
              className="sticky z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 text-left first:border-l-0"
              style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
            >
              #

              {/*
                Row height hangs off the # gutter's BOTTOM edge. It is the one column that is
                about rows rather than about a value, so a horizontal grip here cannot be
                mistaken for a column resizer — and `sticky` already positions this cell, so
                the handle needs no `relative` of its own.
              */}
              <span
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize row height"
                onPointerDown={startRowResize}
                className="absolute -bottom-0.5 left-0 z-10 h-1.5 w-full cursor-row-resize hover:bg-blue-300"
              />
            </th>

            {/*
              A grouped run becomes ONE cell naming the group; the columns under it move to
              the second header row. An ungrouped column spans both rows, so it keeps its
              sort control and its resize grip in the row it already had.
            */}
            {runs.map((run) =>
              run.group === null ? (
                columns
                  .slice(run.start, run.start + run.span)
                  .map((column, offset) => columnHead(column, run.start + offset, headRows))
              ) : (
                <th
                  key={`group-${run.start}`}
                  scope="colgroup"
                  colSpan={run.span}
                  /*
                    Its left edge is the group's left edge, so it takes the outline's tone —
                    and it closes underneath, because the heading and the columns it names are
                    two rows of one header. Without that line the group's name floated over
                    its children rather than heading them.
                  */
                  className="border-b border-l border-slate-300 bg-slate-100 px-3 py-2 text-center"
                >
                  {run.group}
                </th>
              ),
            )}

            {/*
              Actions is always last, fixed width, and pinned to the right edge. Pinning is
              not decoration: once a column is widened the table scrolls horizontally, and an
              unpinned kebab ends up off-screen — unreachable without scrolling sideways to
              reach the primary way to act on a row.
            */}
            <th
              scope="col"
              rowSpan={headRows}
              className="sticky right-0 z-30 border-l border-slate-200 bg-slate-100 px-2 py-2 text-right"
              style={{ width: ACTIONS_WIDTH }}
            >
              <span className="sr-only">Actions</span>
            </th>
          </tr>

          {/*
            The second row: only the columns that sit under a group. Everything else spans
            both rows from above, so these flow into the slots the rowspans left open, in the
            same left-to-right order they were declared in.
          */}
          {headRows === 2 ? (
            <tr className={HEAD_ROW}>
              {runs
                .filter((run) => run.group !== null)
                .flatMap((run) =>
                  columns
                    .slice(run.start, run.start + run.span)
                    .map((column, offset) => columnHead(column, run.start + offset, 1)),
                )}
            </tr>
          ) : null}
        </thead>

        <tbody>
          {loading ? (
            <SkeletonRows
              rows={8}
              columns={columns.length + (selectable ? 3 : 2)}
              height={rowHeight}
            />
          ) : (
            rows.map((row, index) => {
              const key = rowKey(row);
              const isSelected = selected.has(key);

              /*
                A rule between one unit's run of posts and the next.

                Not before the first: a separator above the first group would read as the
                table's own top border drawn twice. One cell spanning every column, because it
                separates ROWS rather than describing any of them — a per-column version would
                break the line at every column border and read as an empty row.

                A step darker than a row's own `slate-100` divider, and no taller: the point is
                that the eye can see where a group ends, not that the table grows a band.
                `aria-hidden`, because a screen reader gets the group from the Unit cell.
              */
              const opensGroup = index > 0 && (groupStarts?.has(index) ?? false);

              return (
                <Fragment key={key}>
                  {opensGroup ? (
                    <tr aria-hidden="true">
                      <td colSpan={fullSpan} className="border-t-2 border-slate-300 p-0" />
                    </tr>
                  ) : null}

                  <tr
                    // A floor, not a clamp: a wrapping cell still grows the row past it.
                    style={{ height: rowHeight }}
                    /*
                      Three weights of horizontal line, and the order between them is the
                      point: a row divider is `slate-200`, a group break is `slate-300` at
                      2px, and the header's own edge is heavier still. Each says how much of
                      a boundary it is, so the eye can tell a row from a group without
                      reading either.

                      `slate-100` was too faint here — a table this wide needs the row line to
                      carry the eye across it — but it must stay clearly lighter than the
                      break, or a group boundary stops being visible as one.
                    */
                    className={`group border-t border-slate-200 hover:bg-slate-50 ${
                      isSelected ? 'bg-blue-50/40' : ''
                    }`}
                  >
                    {selectable ? (
                      <td
                        className="sticky left-0 z-10 border-l border-slate-100 bg-white px-3 py-2 first:border-l-0 group-hover:bg-slate-50"
                        style={{ width: SELECT_WIDTH }}
                      >
                        <Checkbox
                          id={`${module}-select-${key}`}
                          name={`${module}-select-${key}`}
                          checked={isSelected}
                          onChange={() => onToggleRow?.(key)}
                          aria-label="Select row"
                        />
                      </td>
                    ) : null}

                    <td
                      className={`sticky z-10 border-l border-slate-100 bg-white px-3 py-2 ${ROW_ORDINAL} first:border-l-0 group-hover:bg-slate-50`}
                      style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
                    >
                      {firstRow + index}
                    </td>

                    {columns.map((column, columnIndex) => {
                      // Identity and position are separate questions about the same column: the anchor is
                      // always the row's click target, whether or not it is pinned.
                      const isAnchor = columnIndex === anchorIndex;
                      const isFrozen = freezeAnchor && isAnchor;

                      /*
                        A merged column renders one cell for a run of rows: the row that starts
                        the run carries it with a rowSpan, and the rows it covers render nothing
                        at all. Emitting an empty cell instead would push every column after it
                        one place to the right on those rows.
                      */
                      const spans = merged.get(columnIndex);

                      if (spans) {
                        const span = spans.get(index);

                        if (span === undefined) {
                          return null;
                        }

                        return (
                          <td
                            key={column.key}
                            rowSpan={span}
                            /*
                              The padding lives on the CELL, not on the label.

                              A vertical run is usually longer than the rows it spans, so it has
                              to clip — and a label carrying its own padding clips THROUGH it,
                              which put the first character hard against the top border. Padding
                              the cell makes the gap structural: the clip happens inside it, so
                              the text stops short of the border however long the name is.
                            */
                            className="overflow-hidden border-l border-slate-100 bg-white px-1 py-2 text-center align-middle first:border-l-0"
                          >
                            {/*
                              Sideways and reading bottom-to-top — the conventional axis label,
                              and the only orientation that keeps a name legible in a column
                              this narrow. `rotate(180deg)` is what turns vertical-rl's
                              top-to-bottom run the other way up.

                              Inline styles rather than utilities: writing-mode has to survive
                              Tailwind's class detection, and a transform that lands one paint
                              late would flash the label the wrong way round.
                            */}
                            <span
                              /*
                                `max-h-full` is what keeps the run inside the cell's content box
                                — without it the span sizes to its text and overflows the padding
                                it is supposed to sit within. The ellipsis then lands at the end
                                of the visible run rather than mid-glyph at the border.
                              */
                              className="mx-auto inline-block max-h-full overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-medium text-slate-600"
                              style={
                                column.sideways
                                  ? {
                                      writingMode: 'vertical-rl',
                                      textOrientation: 'sideways',
                                      transform: 'rotate(180deg)',
                                    }
                                  : undefined
                              }
                            >
                              {column.render(row, firstRow + index)}
                            </span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={column.key}
                          className={[
                            'border-l px-3 py-2 align-middle first:border-l-0',
                            // A group's outline is a step darker — see groupBorder.
                            groupBorder.has(columnIndex) ? 'border-slate-300' : 'border-slate-100',
                            column.align === 'right' ? 'text-right' : '',
                            isFrozen ? 'sticky z-10 bg-white group-hover:bg-slate-50' : '',
                          ].join(' ')}
                          style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
                        >
                          {column.groupCounter ? (
                          column.render(row, groupOrdinals[index] ?? index + 1)
                        ) : isAnchor ? (
                            <AnchorCellContext.Provider value={true}>
                              {onRowOpen ? (
                                <button
                                  type="button"
                                  id={`${module}-open-${key}`}
                                  onClick={() => onRowOpen(row)}
                                  className="block w-full min-w-0 cursor-pointer rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                                >
                                  {column.render(row, firstRow + index)}
                                </button>
                              ) : (
                                column.render(row, firstRow + index)
                              )}
                            </AnchorCellContext.Provider>
                          ) : (
                            column.render(row, firstRow + index)
                          )}
                        </td>
                      );
                    })}

                    <td className="sticky right-0 z-10 border-l border-slate-100 bg-white px-2 py-2 text-right group-hover:bg-slate-50">
                      <RowActionMenu
                        id={`${module}-actions-${key}`}
                        otherInfo={otherInfo(row)}
                        extraItems={rowActions?.(row)}
                        onEdit={onEdit ? () => onEdit(row) : undefined}
                        editPermission={editPermission}
                        onDelete={onDelete ? () => onDelete(row) : undefined}
                        deletePermission={deletePermission}
                        deleteLabel={deleteLabel?.(row)}
                        deleteDisabledReason={deleteDisabledReason?.(row) ?? null}
                      />
                    </td>
                  </tr>
                </Fragment>
              );
            })
          )}

          {/*
            Takes the stretch that would otherwise inflate every data row. Only present when
            the table is stretched at all, so an ordinary list carries no phantom row.
          */}
          {hasFooter && !loading ? (
            <tr aria-hidden="true" className="h-full">
              <td colSpan={columns.length + (selectable ? 3 : 2)} className="p-0" />
            </tr>
          ) : null}
        </tbody>

        {/* Totals pinned to the bottom edge, so a scrolled list keeps its summary in view. */}
        {hasFooter && !loading ? (
          <tfoot className="sticky bottom-0 z-20">
            <tr className="bg-slate-100 text-[9px] font-semibold uppercase tracking-wider text-gray-600">
              {selectable ? (
                <td
                  className="sticky left-0 z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
                  style={{ width: SELECT_WIDTH }}
                />
              ) : null}

              <td
                className="sticky z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
                style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
              />

              {columns.map((column, index) => {
                const isFrozen = freezeAnchor && index === 0;

                return (
                  <td
                    key={column.key}
                    className={[
                      'border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      isFrozen ? 'sticky z-30' : '',
                    ].join(' ')}
                    style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
                  >
                    {column.footer?.(rows)}
                  </td>
                );
              })}

              <td className="sticky right-0 z-30 border-l border-slate-200 bg-slate-100 px-2 py-2" />
            </tr>
          </tfoot>
        ) : null}
      </table>
    </IndexTableSurfaceContext.Provider>
  );
}
